<template>
  <v-dialog
    v-model="internalValue"
    :max-width="660"
    persistent
    content-class="request-commentary-dialog"
    data-testid="request-commentary-dialog"
  >
    <v-card elevation="3" class="rounded-lg">
      <!-- Header Section -->
      <v-card-title class="primary white--text d-flex align-center py-3 px-4">
        <span class="text-h6 font-weight-medium">{{ heading }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          dark
          small
          @click="internalValue = false"
          data-testid="request-commentary-dialog__close-btn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Loading Overlay -->
      <v-overlay :value="loading" absolute opacity="0.3">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-overlay>

      <!-- Body Content -->
      <v-card-text class="pa-5">
        <!-- Context Tag / Chip -->
        <div class="mb-5">
          <CountSection
            v-if="bulksubmit"
            :count="multiselectcount"
            networkLabel=""
            countLabel="GL’s Selected"
            data-testid="request-commentary-dialog__bulk-chip"
            class="gap-120"
          />
          <div
            v-else
            class="d-flex align-center gap-2 context-tag font-weight-medium"
            data-testid="request-commentary-dialog__gl-chip"
          >
            <v-icon small>mdi-file-document-outline</v-icon>
            {{ glCodeDisplay }}
          </div>
        </div>

        <!-- Local Approver Checkboxes -->
        <div v-if="isapprover || isregionalapprover" class="checkbox-section mb-4">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-3">
            Select reasons for requesting commentary:
          </div>
          <div class="checkbox-list">
            <v-checkbox
              v-model="localControlProforma"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Local control proforma')"
              data-testid="request-commentary-dialog__local-control-proforma"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ localControlsLabel }}</strong> vs <strong>{{ proformaLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="localManagementProforma"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Local management proforma')"
              data-testid="request-commentary-dialog__local-management-proforma"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ localManagementLabel }}</strong> vs <strong>{{ proformaLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="localControlForecast"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Local control forecast')"
              data-testid="request-commentary-dialog__local-control-forecast"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ localControlsLabel }}</strong> vs <strong>{{ forecastLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="localManagementForecast"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Local management forecast')"
              data-testid="request-commentary-dialog__local-management-forecast"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ localManagementLabel }}</strong> vs <strong>{{ forecastLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>

        <!-- Regional Approver Checkboxes -->
        <div v-if="isregionalapprover" class="checkbox-section">
          <v-divider class="mb-4"></v-divider>
          <div class="text-subtitle-2 grey--text text--darken-1 mb-3">
            Regional commentary reasons:
          </div>
          <div class="checkbox-list">
            <v-checkbox
              v-model="regionalControlProforma"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Regional control proforma')"
              data-testid="request-commentary-dialog__regional-control-proforma"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ regionalControlsLabel }}</strong> vs <strong>{{ proformaLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="regionalManagementProforma"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Regional management proforma')"
              data-testid="request-commentary-dialog__regional-management-proforma"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ regionalManagementLabel }}</strong> vs <strong>{{ proformaLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="regionalControlForecast"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Regional control forecast')"
              data-testid="request-commentary-dialog__regional-control-forecast"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ regionalControlsLabel }}</strong> vs <strong>{{ forecastLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="regionalManagementForecast"
              hide-details
              dense
              class="mt-0 mb-2"
              color="primary"
              :disabled="workflowtext.includes('Regional management forecast')"
              data-testid="request-commentary-dialog__regional-management-forecast"
            >
              <template v-slot:label>
                <span class="text-body-2">
                  Commentary requested for purpose of <strong>{{ regionalManagementLabel }}</strong> vs <strong>{{ forecastLabel }}</strong>
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions Section -->
      <v-card-actions class="pa-4 justify-end">
        <v-btn
          text
          @click="internalValue = false"
          data-testid="request-commentary-dialog__cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          elevation="2"
          class="rounded px-6"
          :disabled="requestDisabled"
          :loading="loading"
          @click="$emit('request')"
          data-testid="request-commentary-dialog__request-btn"
        >
          <v-icon left small>mdi-send</v-icon>
          Request
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CountSection from '../views/GlSelection/CountSection.vue';

export default {
  name: 'RequestCommentaryDialog',
  components: {
    CountSection,
  },
  props: {
    value: { type: Boolean, default: false },
    heading: { type: String, default: 'Request Commentary' },
    width: { type: [String, Number], default: 800 },
    loading: { type: Boolean, default: false },
    bulksubmit: { type: Boolean, default: false },
    multiselectcount: { type: Number, default: 0 },
    selectedcommentary: { type: Object, default: () => ({}) },
    isapprover: { type: Boolean, default: false },
    isregionalapprover: { type: Boolean, default: false },
    workflowtext: { type: Array, default: () => [] },
    workflows: { type: Object, default: () => ({}) },
    localControlsLabel: { type: String, default: 'Local Controls' },
    localManagementLabel: { type: String, default: 'Local Management' },
    regionalControlsLabel: { type: String, default: 'Regional Controls' },
    regionalManagementLabel: { type: String, default: 'Regional Management' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
    requestcommentarylocalcontrolvsProforma: { type: Boolean, default: false },
    requestcommentarylocalmanagementvsProforma: { type: Boolean, default: false },
    requestcommentarylocalcontrolvsForecast: { type: Boolean, default: false },
    requestcommentarylocalmanagementvsForecast: { type: Boolean, default: false },
    requestcommentaryregionalcontrolvsProforma: { type: Boolean, default: false },
    requestcommentaryregionalmanagementvsProforma: { type: Boolean, default: false },
    requestcommentaryregionalcontrolvsForecast: { type: Boolean, default: false },
    requestcommentaryregionalmanagementvsForecast: { type: Boolean, default: false },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    localControlProforma: {
      get() {
        return this.requestcommentarylocalcontrolvsProforma;
      },
      set(value) {
        this.$emit('update:requestcommentarylocalcontrolvsProforma', value);
      },
    },
    localManagementProforma: {
      get() {
        return this.requestcommentarylocalmanagementvsProforma;
      },
      set(value) {
        this.$emit('update:requestcommentarylocalmanagementvsProforma', value);
      },
    },
    localControlForecast: {
      get() {
        return this.requestcommentarylocalcontrolvsForecast;
      },
      set(value) {
        this.$emit('update:requestcommentarylocalcontrolvsForecast', value);
      },
    },
    localManagementForecast: {
      get() {
        return this.requestcommentarylocalmanagementvsForecast;
      },
      set(value) {
        this.$emit('update:requestcommentarylocalmanagementvsForecast', value);
      },
    },
    regionalControlProforma: {
      get() {
        return this.requestcommentaryregionalcontrolvsProforma;
      },
      set(value) {
        this.$emit('update:requestcommentaryregionalcontrolvsProforma', value);
      },
    },
    regionalManagementProforma: {
      get() {
        return this.requestcommentaryregionalmanagementvsProforma;
      },
      set(value) {
        this.$emit('update:requestcommentaryregionalmanagementvsProforma', value);
      },
    },
    regionalControlForecast: {
      get() {
        return this.requestcommentaryregionalcontrolvsForecast;
      },
      set(value) {
        this.$emit('update:requestcommentaryregionalcontrolvsForecast', value);
      },
    },
    regionalManagementForecast: {
      get() {
        return this.requestcommentaryregionalmanagementvsForecast;
      },
      set(value) {
        this.$emit('update:requestcommentaryregionalmanagementvsForecast', value);
      },
    },
    requestDisabled() {
      const levelLocalSelected =
        this.localControlForecast ||
        this.localControlProforma ||
        this.localManagementProforma ||
        this.localManagementForecast;
      const levelRegionalSelected =
        this.regionalControlForecast ||
        this.regionalControlProforma ||
        this.regionalManagementProforma ||
        this.regionalManagementForecast;
      if (this.isregionalapprover) {
        return !levelRegionalSelected;
      }
      return !levelLocalSelected;
    },
    /**
     * Display string for the GL code chip.
     * Shows "Code - Name" format.
     */
    glCodeDisplay() {
      const code = this.selectedcommentary?.glcode_detail?.code || '';
      const name = this.selectedcommentary?.glcode_detail?.name || '';
      if (code && name) {
        return `${code} - ${name}`;
      }
      return code || name || 'No GL Selected';
    },
  },
};
</script>

<style scoped>
.request-commentary-dialog .v-card {
  overflow: hidden;
}

.context-tag {
  width: fit-content;
  border-radius: var(--radius-8);
  border: 1px solid var(--grey-900);
  background: var(--grey-100);
  padding: var(--space-3) var(--space-5);
}

.checkbox-section .checkbox-list {
  padding-left: 4px;
}

.checkbox-section .v-input--checkbox {
  margin-top: 0;
}

.checkbox-section .v-input--checkbox .v-label {
  color: rgba(0, 0, 0, 0.87);
}
</style>
